import { forwardRef, useEffect, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cls } from '../lib/format';
import artAssets from '@an/ui/illustrations/empty-assets.svg';
import artTickets from '@an/ui/illustrations/empty-tickets.svg';
import artOffline from '@an/ui/illustrations/empty-offline.svg';
import artDone from '@an/ui/illustrations/empty-done.svg';
import artSearch from '@an/ui/illustrations/empty-search.svg';
import artData from '@an/ui/illustrations/empty-data.svg';

// Small design system (03): accessible, keyboard-complete, color-blind-safe status palette
// (status always pairs a shape/label with color, never red/green alone).

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'danger-outline';
export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md'; busy?: boolean }>(
  ({ variant = 'secondary', size = 'md', busy, className, children, disabled, ...p }, ref) => (
    <button
      ref={ref}
      disabled={disabled || busy}
      className={cls(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium tracking-[-0.01em] transition duration-fast ease-an active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:pointer-events-none disabled:opacity-50 whitespace-nowrap',
        size === 'sm' ? 'h-8 px-3 text-xs' : 'h-9 px-4 text-sm',
        variant === 'primary' && 'bg-accent text-accent-fg shadow-[inset_0_1px_0_rgb(255_255_255/0.18),0_1px_2px_rgb(0_0_0/0.15)] hover:bg-accent-hover hover:shadow-[inset_0_1px_0_rgb(255_255_255/0.18),0_6px_18px_-6px_rgb(var(--an-brand-500)/0.6)]',
        variant === 'secondary' && 'border border-line-strong bg-surface text-fg shadow-sm hover:border-fg-subtle hover:bg-surface-2/60',
        variant === 'danger' && 'bg-red-600 text-white shadow-sm hover:bg-red-700',
        variant === 'danger-outline' && 'border border-red-500/60 bg-surface text-red-600 hover:bg-red-500/10',
        variant === 'ghost' && 'text-fg/80 hover:bg-fg/[0.06] hover:text-fg',
        className,
      )}
      {...p}
    >
      {busy && <Spinner className="h-3.5 w-3.5" />}
      {children}
    </button>
  ),
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...p }, ref) => (
  <input ref={ref} className={cls('w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-fg shadow-sm transition placeholder:text-fg-subtle hover:border-fg-subtle focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 disabled:cursor-not-allowed disabled:bg-surface-2 disabled:opacity-70', 'h-9', className)} {...p} />
));
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...p }, ref) => (
  <textarea ref={ref} className={cls('w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-fg shadow-sm transition placeholder:text-fg-subtle hover:border-fg-subtle focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 disabled:cursor-not-allowed disabled:bg-surface-2 disabled:opacity-70', 'py-2', className)} rows={3} {...p} />
));
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, children, ...p }, ref) => (
  <select ref={ref} className={cls('w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-fg shadow-sm transition placeholder:text-fg-subtle hover:border-fg-subtle focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 disabled:cursor-not-allowed disabled:bg-surface-2 disabled:opacity-70', 'h-9 pr-8', className)} {...p}>
    {children}
  </select>
));

export function Field({ label, hint, error, children, required }: { label: string; hint?: ReactNode; error?: string; children: ReactNode; required?: boolean }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-medium text-fg/85">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1.5 block text-xs text-fg-muted">{hint}</span>}
      {error && <span className="mt-1.5 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

export function Checkbox({ label, checked, onChange, hint }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-line-strong accent-brand-600 focus:ring-brand-500" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <span className="block text-xs text-fg-muted">{hint}</span>}
      </span>
    </label>
  );
}

export function Card({ title, actions, children, className, pad = true }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; pad?: boolean }) {
  return (
    <section className={cls('rounded-xl border border-line bg-surface shadow-an-sm', className)}>
      {(title || actions) && (
        <header className="flex min-h-[48px] items-center justify-between gap-2 border-b border-line px-4 py-2.5">
          <h3 className="text-[13px] font-semibold tracking-[-0.01em] text-fg">{title}</h3>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
      )}
      <div className={pad ? 'p-4' : ''}>{children}</div>
    </section>
  );
}

export function Badge({ children, tone = 'slate', title }: { children: ReactNode; tone?: 'slate' | 'green' | 'amber' | 'red' | 'blue' | 'purple' | 'brand'; title?: string }) {
  const t = { slate: 'bg-fg/[0.06] text-fg/80 ring-fg/10', green: 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/25', amber: 'bg-amber-500/10 text-amber-700 ring-amber-500/25', red: 'bg-red-500/10 text-red-700 ring-red-500/25', blue: 'bg-sky-500/10 text-sky-700 ring-sky-500/25', purple: 'bg-violet-500/10 text-violet-700 ring-violet-500/25', brand: 'bg-brand-500/10 text-brand-700 ring-brand-500/25' }[tone];
  return (
    <span title={title} className={cls('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset', t)}>
      {children}
    </span>
  );
}

/** Connection state dot (05 §9): grey ○ never, green ● online, blue ☾ sleeping, amber ▲ offline, red ■ >7 d. */
export function IotDot({ state, withLabel = false }: { state: string; withLabel?: boolean }) {
  const map: Record<string, { c: string; s: string; l: string }> = {
    online: { c: 'text-emerald-600', s: '●', l: 'Online' },
    backfilling: { c: 'text-emerald-600', s: '◐', l: 'Catching up' },
    sleeping: { c: 'text-sky-600', s: '☾', l: 'Sleeping' },
    offline: { c: 'text-amber-600', s: '▲', l: 'Offline' },
    offline_long: { c: 'text-red-700', s: '■', l: 'Offline > 7 d' },
    never_connected: { c: 'text-slate-400', s: '○', l: 'Awaiting first data' },
    no_device: { c: 'text-slate-300', s: '–', l: 'No device' },
    quarantined: { c: 'text-red-700', s: '◇', l: 'Quarantined' },
    unattached: { c: 'text-slate-500', s: '◌', l: 'Not attached' },
  };
  const m = map[state] ?? map.never_connected!;
  return (
    <span className={cls('inline-flex items-center gap-1 whitespace-nowrap', m.c)} title={m.l} aria-label={m.l}>
      {state === 'online' ? (
        <span aria-hidden className="relative inline-flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60 motion-reduce:animate-none" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
      ) : (
        <span aria-hidden>{m.s}</span>
      )}
      {withLabel && <span className="text-xs text-fg/80">{m.l}</span>}
    </span>
  );
}

export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={cls('animate-spin text-current', className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" />
    </svg>
  );
}

export function Skeleton({ className = 'h-4 w-full' }: { className?: string }) {
  return <div className={cls('animate-pulse rounded-md bg-fg/[0.07]', className)} />;
}

/** Every widget: skeleton → data, with a 15 s timeout → inline error + Retry (03, 11). */
export function Async<T>({ q, children, empty, rows = 3 }: { q: { data?: T; isLoading: boolean; error: unknown; refetch: () => void; isFetching?: boolean }; children: (d: T) => ReactNode; empty?: ReactNode; rows?: number }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!q.isLoading) return setSlow(false);
    const t = setTimeout(() => setSlow(true), 15_000);
    return () => clearTimeout(t);
  }, [q.isLoading]);
  if (q.error || slow)
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm text-red-700">
        <span>{slow ? 'This is taking longer than 15 s.' : ((q.error as Error)?.message ?? 'Could not load')}</span>
        <Button size="sm" onClick={() => { setSlow(false); q.refetch(); }}>Retry</Button>
      </div>
    );
  if (q.isLoading || q.data === undefined)
    return (
      <div className="space-y-2">
        {Array.from({ length: rows }, (_, i) => <Skeleton key={i} />)}
      </div>
    );
  const d = q.data as unknown;
  if (empty && ((Array.isArray(d) && d.length === 0) || ((d as { items?: unknown[] })?.items && (d as { items: unknown[] }).items.length === 0))) return <>{empty}</>;
  return <>{children(q.data)}</>;
}

const EMPTY_ART = { assets: artAssets, tickets: artTickets, offline: artOffline, done: artDone, search: artSearch, data: artData };
export type EmptyArt = keyof typeof EMPTY_ART;

export function Empty({ title, text, action, docs, art = 'data' }: { title: string; text?: string; action?: ReactNode; docs?: string; art?: EmptyArt }) {
  return (
    <div className="anim-fade flex flex-col items-center justify-center gap-2 py-12 text-center">
      {/* navy line art: always on a light plate so it reads in dark mode too */}
      <div className="mb-2 grid h-24 w-24 place-items-center rounded-3xl bg-[#fff] ring-1 ring-line" aria-hidden><img src={EMPTY_ART[art]} alt="" className="h-16 w-16" /></div>
      <div className="font-semibold text-fg">{title}</div>
      {text && <p className="max-w-md text-sm text-fg-muted">{text}</p>}
      <div className="mt-2 flex gap-2">
        {action}
        {docs && <a className="text-sm font-medium text-accent hover:underline" href={docs} target="_blank" rel="noreferrer">Docs</a>}
      </div>
    </div>
  );
}

export function PageHeader({ title, count, actions, children }: { title: string; count?: number | null; actions?: ReactNode; children?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-baseline gap-2.5">
        <h1 className="text-2xl font-semibold tracking-[-0.025em] text-fg">{title}</h1>
        {count !== undefined && count !== null && <span className="tabular rounded-full bg-fg/[0.06] px-2 py-0.5 text-xs font-medium text-fg-muted">{count.toLocaleString()}</span>}
        {children}
      </div>
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="anim-fade fixed inset-0 z-50 flex items-end justify-center bg-ink-950/50 p-0 backdrop-blur-[2px] sm:items-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal aria-label={title} className={cls('anim-in max-h-[92vh] w-full overflow-auto rounded-t-2xl border border-line bg-surface shadow-an-lg sm:rounded-2xl', wide ? 'sm:max-w-3xl' : 'sm:max-w-lg')}>
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-surface/95 px-5 py-3.5 backdrop-blur">
          <h2 className="font-semibold tracking-[-0.01em] text-fg">{title}</h2>
          <button className="grid h-8 w-8 place-items-center rounded-lg text-fg-muted transition hover:bg-fg/[0.06] hover:text-fg" onClick={onClose} aria-label="Close"><svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button>
        </header>
        <div className="p-5">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-line bg-bg/60 px-5 py-3">{footer}</footer>}
      </div>
    </div>
  );
}

/** Delete confirm that requires typing the record name (03 Detail pattern). */
export function ConfirmDelete({ open, onClose, name, onConfirm, what = 'record', busy }: { open: boolean; onClose: () => void; name: string; onConfirm: () => void; what?: string; busy?: boolean }) {
  const [v, setV] = useState('');
  useEffect(() => setV(''), [open]);
  return (
    <Modal open={open} onClose={onClose} title={`Delete ${what}`} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" disabled={v !== name} busy={busy} onClick={onConfirm}>Delete</Button></>}>
      <p className="mb-3 text-sm text-fg-muted">This cannot be undone from the UI. Type <strong className="font-mono">{name}</strong> to confirm.</p>
      <Input autoFocus value={v} onChange={(e) => setV(e.target.value)} />
    </Modal>
  );
}

export function Confirm({ open, onClose, title, text, onConfirm, danger, busy, confirmLabel = 'Confirm' }: { open: boolean; onClose: () => void; title: string; text: ReactNode; onConfirm: () => void; danger?: boolean; busy?: boolean; confirmLabel?: string }) {
  return (
    <Modal open={open} onClose={onClose} title={title} footer={<><Button onClick={onClose}>Cancel</Button><Button variant={danger ? 'danger' : 'primary'} busy={busy} onClick={onConfirm}>{confirmLabel}</Button></>}>
      <div className="text-sm text-fg/85">{text}</div>
    </Modal>
  );
}

export function Tabs({ tabs, value, onChange }: { tabs: Array<{ key: string; label: string; hidden?: boolean }>; value: string; onChange: (k: string) => void }) {
  return (
    <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-line">
      {tabs.filter((t) => !t.hidden).map((t) => (
        <button key={t.key} role="tab" aria-selected={value === t.key} onClick={() => onChange(t.key)} className={cls('-mb-px whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition', value === t.key ? 'border-brand-500 text-fg' : 'border-transparent text-fg-muted hover:text-fg')}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Kpi({ label, value, unit, hint, delta, tone }: { label: string; value: ReactNode; unit?: string; hint?: string; delta?: number | null; tone?: 'red' | 'amber' }) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-line bg-surface p-4 shadow-an-sm transition hover:border-brand-500/40" title={hint}>
      <div className="flex items-center gap-1 text-xs font-medium text-fg-muted">
        {label}
        {hint && <span className="cursor-help text-fg-subtle" aria-label={hint}>ⓘ</span>}
      </div>
      <div className={cls('tabular mt-2 font-mono text-[28px] font-semibold leading-none tracking-[-0.03em] text-fg', tone === 'red' && 'text-red-600', tone === 'amber' && 'text-amber-600')}>
        {value}
        {unit && <span className="ml-1 font-sans text-sm font-normal tracking-normal text-fg-muted">{unit}</span>}
      </div>
      {delta !== undefined && delta !== null && <div className={cls('mt-2 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold', delta >= 0 ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-red-700')}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}% <span className="font-normal text-fg-muted">vs previous</span></div>}
    </div>
  );
}

export function Dl({ items }: { items: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[minmax(110px,auto)_1fr] gap-x-4 gap-y-1.5 text-sm">
      {items.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-fg-muted">{k}</dt>
          <dd className="min-w-0 break-words text-fg">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Menu({ label, items }: { label: ReactNode; items: Array<{ label: string; onClick: () => void; hidden?: boolean; danger?: boolean }> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>{label}</Button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div role="menu" className="anim-in absolute right-0 z-40 mt-1.5 min-w-[220px] rounded-xl border border-line bg-surface p-1 shadow-an-lg">
            {items.filter((i) => !i.hidden).map((i) => (
              <button key={i.label} role="menuitem" className={cls('block w-full rounded-lg px-3 py-2 text-left text-sm text-fg transition hover:bg-fg/[0.06]', i.danger && 'text-red-600')} onClick={() => { setOpen(false); i.onClick(); }}>
                {i.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
